import { useReducer } from "react";

import { DepositOptionDraft } from "../types";

export interface DepositOptionDraftState {
  readonly storeCode: number | undefined;
  readonly overrides: DepositOptionDraft;
}

export type DepositOptionDraftAction =
  | { type: "SET_STORE"; payload: number | undefined }
  | { type: "TOGGLE"; payload: { operationCode: number; active: boolean } }
  | { type: "SAVED" };

export const INITIAL_DEPOSIT_OPTION_DRAFT_STATE: DepositOptionDraftState = {
  storeCode: undefined,
  overrides: {},
};

/**
 * Guarda apenas o que o usuário mexeu (`overrides`), nunca uma cópia do que veio
 * do servidor. O valor exibido é derivado no render (servidor + overrides), então
 * uma revalidação da query no meio da edição não descarta as alterações.
 */
export function depositOptionDraftReducer(
  state: DepositOptionDraftState,
  action: DepositOptionDraftAction,
): DepositOptionDraftState {
  if (action.type === "SET_STORE") {
    return { storeCode: action.payload, overrides: {} };
  }

  if (action.type === "TOGGLE") {
    return {
      ...state,
      overrides: {
        ...state.overrides,
        [action.payload.operationCode]: action.payload.active,
      },
    };
  }

  if (action.type === "SAVED") {
    return { ...state, overrides: {} };
  }

  return state;
}

export interface DepositOptionDraftMethods {
  readonly setStore: (storeCode: number | undefined) => void;
  readonly toggle: (operationCode: number, active: boolean) => void;
  readonly markSaved: () => void;
}

export interface UseDepositOptionDraftReducer {
  readonly state: DepositOptionDraftState;
  readonly methods: DepositOptionDraftMethods;
}

export function useDepositOptionDraftReducer(): UseDepositOptionDraftReducer {
  const [state, dispatch] = useReducer(
    depositOptionDraftReducer,
    INITIAL_DEPOSIT_OPTION_DRAFT_STATE,
  );

  const methods: DepositOptionDraftMethods = {
    setStore: (storeCode: number | undefined): void =>
      dispatch({ type: "SET_STORE", payload: storeCode }),

    toggle: (operationCode: number, active: boolean): void =>
      dispatch({ type: "TOGGLE", payload: { operationCode, active } }),

    markSaved: (): void => dispatch({ type: "SAVED" }),
  };

  return { state, methods };
}
